import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, Tabs, Select, Table, Tag, Progress, Statistic, Row, Col, Button, Empty, message } from 'antd';
import { CheckCircleOutlined, WarningOutlined, ArrowRightOutlined } from '@ant-design/icons';
import QoCService from '../../services/QoCService';

const { TabPane } = Tabs;
const { Option } = Select;

const REGIONS = ['Hhohho', 'Manzini', 'Lubombo', 'Shiselweni'];
const SITE_TYPES = [
    'Clinic without Maternity', 'Clinic with Maternity', 'Health Centre', 'Hospital',
    'National Referral Hospital', 'Private Hospital', 'Public Health Unit',
    'Regional Referral Hospital', 'Specialized Clinic', 'Specialized Hospital',
];

// Same categories as the Facility Profile checklist — used here purely to
// group the flat services-analytics response for display.
const SERVICE_CATEGORIES = [
    { category: 'Outpatient & General', services: [
        'OPD (Outpatient Department)', 'Emergency / Casualty', 'Inpatient Wards', 'Surgery / Theatre',
    ]},
    { category: 'Reproductive, Maternal, Newborn & Child Health', services: [
        'Family Planning', 'ANC (Antenatal Care)', 'PMTCT (Prevention of Mother-to-Child Transmission)',
        'Labour & Delivery', 'Postnatal / Newborn Care', 'MCH / Maternity (general)',
        'Immunisation', 'Under-5 / Child Health Services', 'Adolescent Health Services',
    ]},
    { category: 'Communicable Diseases', services: [
        'ARV / HIV', 'TB Treatment', 'Malaria', 'STIs (other than HIV)', 'Neglected Tropical Diseases (NTDs)',
    ]},
    { category: 'Non-Communicable Diseases', services: [
        'Diabetes', 'Cardiovascular Disease / Hypertension', 'Chronic Respiratory Disease (Asthma/COPD)',
        'Cancer Screening & Treatment',
    ]},
    { category: 'Mental & Neurological Health', services: [
        'Mental Health', 'Neurological Conditions (epilepsy, dementia)',
    ]},
    { category: 'Special Needs / Protection', services: [
        'Palliative Care', 'Rehabilitation / Physiotherapy',
        'GBV / Rape & Intimate Partner Violence Survivor Care', 'Services for Children Affected by Maltreatment',
    ]},
    { category: 'Diagnostics & Support Services', services: [
        'Laboratory', 'Radiology / X-Ray / Imaging', 'Blood Transfusion', 'Pharmacy',
    ]},
    { category: 'Other', services: [
        'Dental / Oral Health', 'Nutrition (Clinical Nutrition/Dietetics)',
    ]},
];

const fillColor = (pct) => {
    if (pct == null) return '#d9d9d9';
    if (pct >= 80) return '#52c41a';
    if (pct >= 60) return '#a0d911';
    if (pct >= 40) return '#faad14';
    if (pct >= 20) return '#fa8c16';
    return '#f5222d';
};

export default function QoCFacilityProfileDashboard() {
    const history = useHistory();
    const [region, setRegion] = useState(null);
    const [siteType, setSiteType] = useState(null);

    const [coverage, setCoverage] = useState([]);
    const [services, setServices] = useState(null);
    const [staffing, setStaffing] = useState(null);
    const [loading, setLoading] = useState(false);

    const filters = { region: region || undefined, site_type: siteType || undefined };

    const loadAll = () => {
        setLoading(true);
        Promise.all([
            QoCService.getFacilityProfileCoverage(filters),
            QoCService.getServicesAnalytics(filters),
            QoCService.getStaffingAnalytics(filters),
        ]).then(([cRes, sRes, stRes]) => {
            setCoverage(cRes.data?.data || []);
            setServices(sRes.data?.data || null);
            setStaffing(stRes.data?.data || null);
        }).catch(() => message.error('Failed to load dashboard data'))
          .finally(() => setLoading(false));
    };

    useEffect(() => { loadAll(); }, [region, siteType]); // eslint-disable-line

    const viewFacility = (facilityId) => {
        history.push(`/qoc/facility-profile?facility=${facilityId}`);
    };

    // ── Coverage tab ────────────────────────────────────────────────────────
    const servicesComplete = coverage.filter(c => c.services_complete).length;
    const staffingComplete = coverage.filter(c => c.staffing_complete).length;

    const coverageCols = [
        { title: 'Facility', dataIndex: 'business_name' },
        { title: 'Region', dataIndex: 'region', width: 110 },
        { title: 'Level', dataIndex: 'site_type', width: 180 },
        { title: 'Services', width: 160, render: (_, r) => (
            <div>
                <Progress percent={Math.round(r.services_answered / r.services_total * 100)}
                    size="small" strokeColor={fillColor(r.services_answered / r.services_total * 100)}
                    format={() => `${r.services_answered}/${r.services_total}`} />
            </div>
        )},
        { title: 'Staffing', width: 160, render: (_, r) => (
            <div>
                <Progress percent={Math.round(r.staffing_answered / r.staffing_total * 100)}
                    size="small" strokeColor={fillColor(r.staffing_answered / r.staffing_total * 100)}
                    format={() => `${r.staffing_answered}/${r.staffing_total}`} />
            </div>
        )},
        { title: 'Status', width: 110, render: (_, r) => (
            r.services_complete && r.staffing_complete
                ? <Tag color="green" icon={<CheckCircleOutlined />}>Complete</Tag>
                : <Tag color="orange" icon={<WarningOutlined />}>Incomplete</Tag>
        )},
        { title: '', width: 90, render: (_, r) => (
            <Button size="small" icon={<ArrowRightOutlined />} onClick={() => viewFacility(r.facility_id)}>View</Button>
        )},
    ];

    // ── Services tab ────────────────────────────────────────────────────────
    const serviceRowsByName = {};
    (services?.services || []).forEach(s => { serviceRowsByName[s.service] = s; });

    // ── Staffing tab ─────────────────────────────────────────────────────────
    const cadreCols = [
        { title: 'Cadre', dataIndex: 'cadre' },
        { title: 'Approved (total)', dataIndex: 'approved', width: 140, align: 'center' },
        { title: 'Actual (total)', dataIndex: 'actual', width: 140, align: 'center' },
        { title: 'Fill Rate', width: 180, render: (_, r) => (
            r.fill_rate == null
                ? <span style={{ color: '#ccc' }}>No data</span>
                : <Progress percent={r.fill_rate} size="small" strokeColor={fillColor(r.fill_rate)} />
        )},
        { title: 'Facilities Reporting', dataIndex: 'facilities_reporting', width: 150, align: 'center' },
    ];

    const rankedCols = [
        { title: 'Facility', dataIndex: 'business_name' },
        { title: 'Region', dataIndex: 'region', width: 110 },
        { title: 'Level', dataIndex: 'site_type', width: 180 },
        { title: 'Approved', dataIndex: 'approved', width: 100, align: 'center' },
        { title: 'Actual', dataIndex: 'actual', width: 100, align: 'center' },
        { title: 'Fill Rate', width: 160, render: (_, r) => (
            r.fill_rate == null ? '—' : <Progress percent={r.fill_rate} size="small" strokeColor={fillColor(r.fill_rate)} />
        )},
        { title: '', width: 90, render: (_, r) => (
            <Button size="small" icon={<ArrowRightOutlined />} onClick={() => viewFacility(r.facility_id)}>View</Button>
        )},
    ];

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{ margin: 0 }}>Facility Profiling Dashboard</h4>
                    <small className="text-muted">Coverage, service availability, and staffing across all facilities</small>
                </div>
            </div>
            <div className="page-pad">
                <Card size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={12}>
                        <Col span={6}>
                            <label style={{ fontWeight: 700, display: 'block', marginBottom: 4, fontSize: 12 }}>Region</label>
                            <Select allowClear placeholder="All Regions" style={{ width: '100%' }} value={region} onChange={setRegion}>
                                {REGIONS.map(r => <Option key={r} value={r}>{r}</Option>)}
                            </Select>
                        </Col>
                        <Col span={8}>
                            <label style={{ fontWeight: 700, display: 'block', marginBottom: 4, fontSize: 12 }}>Facility Level</label>
                            <Select allowClear placeholder="All Levels" style={{ width: '100%' }} value={siteType} onChange={setSiteType}>
                                {SITE_TYPES.map(s => <Option key={s} value={s}>{s}</Option>)}
                            </Select>
                        </Col>
                    </Row>
                </Card>

                <Tabs defaultActiveKey="coverage">
                    <TabPane tab="Coverage" key="coverage">
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={8}>
                                <Card size="small">
                                    <Statistic title="Facilities in view" value={coverage.length} />
                                </Card>
                            </Col>
                            <Col span={8}>
                                <Card size="small">
                                    <Statistic title="Services checklist complete" value={servicesComplete}
                                        suffix={`/ ${coverage.length}`}
                                        valueStyle={{ color: fillColor(coverage.length ? servicesComplete/coverage.length*100 : null) }} />
                                </Card>
                            </Col>
                            <Col span={8}>
                                <Card size="small">
                                    <Statistic title="Staffing breakdown complete" value={staffingComplete}
                                        suffix={`/ ${coverage.length}`}
                                        valueStyle={{ color: fillColor(coverage.length ? staffingComplete/coverage.length*100 : null) }} />
                                </Card>
                            </Col>
                        </Row>
                        <Table rowKey="facility_id" columns={coverageCols} dataSource={coverage}
                            loading={loading} size="small" pagination={{ pageSize: 15 }} />
                    </TabPane>

                    <TabPane tab="Services" key="services">
                        {!services ? <Empty description="No data" /> : (
                            <>
                                <div style={{ marginBottom: 12, color: '#888', fontSize: 12 }}>
                                    Based on {services.total_facilities} facilities in view. "% Available" is calculated
                                    against facilities where the service is applicable (excludes N/A and unanswered).
                                </div>
                                {SERVICE_CATEGORIES.map(cat => (
                                    <Card key={cat.category} size="small" title={cat.category} style={{ marginBottom: 12 }}>
                                        <Table size="small" pagination={false} rowKey="service"
                                            dataSource={cat.services.map(s => serviceRowsByName[s]).filter(Boolean)}
                                            columns={[
                                                { title: 'Service', dataIndex: 'service' },
                                                { title: 'Yes', dataIndex: 'yes', width: 70, align: 'center' },
                                                { title: 'No', dataIndex: 'no', width: 70, align: 'center' },
                                                { title: 'N/A', dataIndex: 'na', width: 70, align: 'center' },
                                                { title: 'Unanswered', dataIndex: 'unanswered', width: 100, align: 'center' },
                                                { title: '% Available', width: 180, render: (_, r) => (
                                                    r.pct_available == null
                                                        ? <span style={{ color: '#ccc' }}>No data</span>
                                                        : <Progress percent={r.pct_available} size="small" strokeColor={fillColor(r.pct_available)} />
                                                )},
                                            ]} />
                                    </Card>
                                ))}
                            </>
                        )}
                    </TabPane>

                    <TabPane tab="Staffing" key="staffing">
                        {!staffing ? <Empty description="No data" /> : (
                            <>
                                <Card size="small" title="National / Filtered Fill Rate by Cadre" style={{ marginBottom: 16 }}>
                                    <Table rowKey="cadre" columns={cadreCols} dataSource={staffing.by_cadre} size="small" pagination={false} />
                                </Card>
                                <Card size="small" title="Facilities Ranked by Fill Rate (lowest first)">
                                    {staffing.by_facility_ranked.length === 0
                                        ? <Empty description="No facilities have reported staffing data yet" />
                                        : <Table rowKey="facility_id" columns={rankedCols} dataSource={staffing.by_facility_ranked}
                                            size="small" pagination={{ pageSize: 15 }} />}
                                </Card>
                            </>
                        )}
                    </TabPane>
                </Tabs>
            </div>
        </div>
    );
}
